import React from "react";
import * as THREE from "three";
import { AlertTriangle } from "lucide-react";
import DraggablePanel from "./DraggablePanel";
import Button from "../ui/Button";
import { useSelectionStore } from "../../stores/selectionStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import {
  useMeasurementActions,
  useMeasurementToolState,
  useStaticInterferenceState,
} from "../../stores/measurementStore";
import { getViewerHandles } from "../../runtime/viewerHandles";
import { safeTraverse } from "../../viewer/scene/sceneObjectLookups";

const formatNumber = (value, digits = 4) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
};

const formatMeters = (value) => `${formatNumber(value, 5)} m`;
const formatVector = (vector) =>
  Array.isArray(vector) ? vector.map((value) => formatMeters(value)).join(" x ") : "0 m";

function expandBoxForObjects(objects) {
  const box = new THREE.Box3();
  let hasObject = false;
  objects.forEach((object) => {
    if (!object) return;
    const next = new THREE.Box3().setFromObject(object);
    if (next.isEmpty()) return;
    box.union(next);
    hasObject = true;
  });
  if (!hasObject || box.isEmpty()) return null;
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);
  return {
    size: [size.x, size.y, size.z],
    center: [center.x, center.y, center.z],
  };
}

function getObjectsForSelection({ selectedBody, selectedMesh, selectedGeom }) {
  const scene = getViewerHandles().scene;
  if (!scene) return { label: "Viewport not ready", objects: [] };
  const objects = [];
  let label = "No selection";

  safeTraverse(scene, (object) => {
    const userData = object?.userData || {};
    if (selectedBody && userData.bodyUuid && String(userData.bodyUuid) === String(selectedBody)) {
      if (object.isMesh) objects.push(object);
      label = "Selected body";
      return;
    }
    if (selectedMesh && userData.meshName && String(userData.meshName) === String(selectedMesh)) {
      if (object.isMesh) objects.push(object);
      label = "Selected mesh";
      return;
    }
    if (selectedGeom) {
      const geomKey = String(selectedGeom);
      const geomUuid = geomKey.includes(":") ? geomKey.split(":").pop() : geomKey;
      const matchesGeom =
        (userData.geomUuid && String(userData.geomUuid) === geomUuid) ||
        (userData.geomName && String(userData.geomName) === geomKey);
      if (matchesGeom && object.isMesh) {
        objects.push(object);
        label = "Selected geom";
      }
    }
  });

  return { label, objects };
}

function useSelectionBoundingBox() {
  const selectedBody = useSelectionStore((state) => state.selectedBody);
  const selectedMesh = useSelectionStore((state) => state.selectedMesh);
  const selectedGeom = useSelectionStore((state) => state.selectedGeom);
  return React.useMemo(() => {
    const { label, objects } = getObjectsForSelection({ selectedBody, selectedMesh, selectedGeom });
    return {
      label,
      bbox: expandBoxForObjects(objects),
      objectCount: objects.length,
    };
  }, [selectedBody, selectedGeom, selectedMesh]);
}

const ToolButton = ({ active, children, onClick }) => (
  <Button
    variant={active ? "secondary" : "ghost"}
    size="small"
    className="!w-auto min-w-[5.5rem] justify-center"
    onClick={onClick}
  >
    {children}
  </Button>
);

const MeasurementsPanel = ({ open, onClose, titleIcon, mobilePinPanelId }) => {
  const { activeTool, currentPoints, measurements } = useMeasurementToolState();
  const { status, result, error } = useStaticInterferenceState();
  const { setActiveTool, clearMeasurements, computeStaticInterference, clearStaticInterference } =
    useMeasurementActions();
  const isPhysicsRunning = usePhysicsStore((state) => state.isRunning || state.isPaused);
  const selectBody = useSelectionStore((state) => state.selectBody);
  const { label: bboxLabel, bbox, objectCount } = useSelectionBoundingBox();
  const canCheckInterference = status !== "checking" && !isPhysicsRunning;

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Measurements"
      titleIcon={titleIcon}
      initialSize={{ width: 460, height: 560 }}
      minWidth={360}
      minHeight={340}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="min-h-0 flex-1 space-y-4 overflow-auto p-3 text-gray-200">
        <section className="space-y-2 border-b border-gray-800 pb-4">
          <div className="text-sm font-semibold text-gray-100">Surface measurements</div>
          <div className="text-xs leading-5 text-gray-400">
            Activate a tool, then click surface points in the viewport. Measurements are
            session-only.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ToolButton
              active={activeTool === "distance"}
              onClick={() => setActiveTool(activeTool === "distance" ? null : "distance")}
            >
              Distance
            </ToolButton>
            <ToolButton
              active={activeTool === "angle"}
              onClick={() => setActiveTool(activeTool === "angle" ? null : "angle")}
            >
              Angle
            </ToolButton>
            <Button
              variant="ghost"
              size="small"
              className="!w-auto min-w-[4.5rem] justify-center"
              onClick={clearMeasurements}
            >
              Clear
            </Button>
          </div>
          {activeTool ? (
            <div className="rounded border border-sky-800/70 bg-sky-950/30 px-3 py-2 text-xs text-sky-100">
              {activeTool === "distance"
                ? `Pick ${2 - currentPoints.length} more point(s) for distance.`
                : `Pick ${3 - currentPoints.length} more point(s) for angle.`}
            </div>
          ) : null}
          <div className="space-y-2 pt-1">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">Results</div>
            {measurements.map((measurement, index) => (
              <div
                key={measurement.id}
                className="rounded border border-gray-800 bg-gray-900/50 p-2"
              >
                <div className="text-xs font-medium text-gray-100">
                  {index + 1}. {measurement.type === "distance" ? "Distance" : "Angle"}
                </div>
                <div className="mt-1 font-mono text-sm text-sky-100">
                  {measurement.type === "distance"
                    ? formatMeters(measurement.meters)
                    : `${formatNumber(measurement.degrees, 2)} deg`}
                </div>
              </div>
            ))}
            {measurements.length === 0 ? (
              <div className="rounded border border-gray-800 bg-gray-900/40 px-3 py-3 text-xs text-gray-500">
                No distance or angle measurements yet.
              </div>
            ) : null}
          </div>
        </section>

        <section className="space-y-2 border-b border-gray-800 pb-4">
          <div className="text-sm font-semibold text-gray-100">Selection bounding box</div>
          {bbox ? (
            <div className="rounded border border-gray-700 bg-gray-900/60 p-3 text-xs">
              <div className="text-gray-400">
                {bboxLabel} ({objectCount} render object{objectCount === 1 ? "" : "s"})
              </div>
              <div className="mt-2 font-mono text-gray-100">Size: {formatVector(bbox.size)}</div>
              <div className="mt-1 font-mono text-gray-400">
                Center: {bbox.center.map((value) => formatNumber(value, 4)).join(", ")}
              </div>
            </div>
          ) : (
            <div className="rounded border border-gray-800 bg-gray-900/40 px-3 py-2 text-xs text-gray-400">
              Select a body, mesh, or geom to show its world-axis bounding box.
            </div>
          )}
        </section>

        <section className="space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-100">Static interference</div>
              <div className="mt-1 text-xs leading-5 text-gray-400">
                Checks current pose in MuJoCo without starting playback.
              </div>
            </div>
            <Button
              variant="secondary"
              size="small"
              className="!w-auto min-w-[4.5rem] justify-center"
              onClick={() => void computeStaticInterference()}
              disabled={!canCheckInterference}
              title={isPhysicsRunning ? "Stop physics before checking interference" : undefined}
            >
              {status === "checking" ? "Checking..." : "Check"}
            </Button>
          </div>
          {isPhysicsRunning ? (
            <div className="rounded border border-sky-800/70 bg-sky-950/30 px-3 py-2 text-xs text-sky-100">
              Static interference checks are disabled while physics is running.
            </div>
          ) : null}
          {status === "error" && error ? (
            <div className="rounded border border-red-800/70 bg-red-950/30 px-3 py-2 text-xs text-red-100">
              {error}
            </div>
          ) : null}
          {result?.missingMeshes?.length || (result?.loadMode && result.loadMode !== "full") ? (
            <div className="flex gap-2 rounded border border-amber-700/70 bg-amber-950/30 px-3 py-2 text-xs text-amber-100">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" />
              <span>
                Worker ran in {result.loadMode} mode
                {result.missingMeshes.length
                  ? ` because ${result.missingMeshes.length} mesh asset(s) were missing.`
                  : "."}{" "}
                Mesh-based interference may be incomplete.
              </span>
            </div>
          ) : null}
          <div className="space-y-2 pt-1">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">Results</div>
            {result ? (
              <>
                <div className="rounded border border-gray-800 bg-gray-900/50 p-2 text-xs">
                  <div className="text-gray-100">
                    {result.penetrationCount === 0
                      ? "No penetrating contacts found."
                      : `${result.penetrationCount} penetrating contact(s) found.`}
                  </div>
                  <div className="mt-1 text-gray-500">
                    {result.contactCount} total contact(s), checked{" "}
                    {new Date(result.computedAt).toLocaleTimeString()}
                  </div>
                  <Button
                    variant="ghost"
                    size="small"
                    className="mt-2 !w-auto justify-center"
                    onClick={clearStaticInterference}
                  >
                    Clear interference
                  </Button>
                </div>
                {result.contacts.map((contact) => (
                  <button
                    key={`${contact.index}:${contact.geom1}:${contact.geom2}`}
                    type="button"
                    className="w-full rounded border border-red-900/70 bg-red-950/20 p-2 text-left text-xs hover:bg-red-950/40"
                    onClick={() => {
                      const bodyUuid = contact.body1Uuid || contact.body2Uuid;
                      if (bodyUuid) selectBody(bodyUuid);
                    }}
                  >
                    <div className="font-medium text-red-100">
                      {contact.body1DisplayName || contact.body1Name}{" "}
                      <span className="text-gray-500">vs</span>{" "}
                      {contact.body2DisplayName || contact.body2Name}
                    </div>
                    <div className="mt-1 font-mono text-red-200">
                      penetration {formatMeters(Math.abs(contact.distance))}
                    </div>
                    <div className="mt-1 truncate text-gray-500">
                      geoms {contact.geom1Name || contact.geom1} /{" "}
                      {contact.geom2Name || contact.geom2}
                    </div>
                  </button>
                ))}
              </>
            ) : (
              <div className="rounded border border-gray-800 bg-gray-900/40 px-3 py-3 text-xs text-gray-500">
                Run a static interference check to populate this list.
              </div>
            )}
          </div>
        </section>
      </div>
    </DraggablePanel>
  );
};

export default React.memo(MeasurementsPanel);
